// hero.jsx — three switchable hero variants: centered / split / terminal

import React from "react";
import { PresenceBadge, usePresence } from "./live.jsx";
const { useState: useStateHero, useEffect: useEffectHero, useRef: useRefHero } = React;

// slug = simple-icons id, which is also the filename in wwwroot/img/stack; label = hover text.
//
// These used to be fetched per page load from api.iconify.design, an origin the enforcing CSP in
// SecurityHeaders.cs never allowlisted — so every icon here was blocked and the strip rendered
// empty. Self-hosting fixes that without widening img-src, and drops a third party that saw every
// visitor's IP. cdn.simpleicons.org, which IS allowlisted, is not an option: it has dropped
// csharp, microsoftsqlserver, playwright and openai.
//
// Adding a tool means adding wwwroot/img/stack/<slug>.svg next to the entry. The committed files
// are Iconify's simple-icons renders with the --text-secondary tint (A9B4D0) baked in, the same
// colour ContactIconSets.SimpleIconsTint asks that CDN for:
//   curl -o wwwroot/img/stack/<slug>.svg \
//        "https://api.iconify.design/simple-icons/<slug>.svg?color=%23A9B4D0"
const STACK = [
  { slug: "csharp", label: "C#" },
  { slug: "cplusplus", label: "C++" },
  { slug: "dotnet", label: ".NET" },
  { slug: "microsoftsqlserver", label: "MS SQL Server" },
  { slug: "sqlite", label: "SQLite" },
  { slug: "postgresql", label: "PostgreSQL" },
  { slug: "git", label: "Git" },
  { slug: "react", label: "React" },
  { slug: "typescript", label: "TypeScript" },
  { slug: "cypress", label: "Cypress" },
  { slug: "playwright", label: "Playwright" },
  { slug: "openai", label: "OpenAI" },
  { slug: "claude", label: "Claude" },
  { slug: "jira", label: "Jira" },
  { slug: "atlassian", label: "Atlassian" },
  { slug: "bitbucket", label: "Bitbucket" },
  { slug: "github", label: "GitHub" },
  { slug: "selenium", label: "Selenium" },
  { slug: "apachejmeter", label: "JMeter" },
  { slug: "bruno", label: "Bruno" },
];

// auto-scroll speed is derived from track width so the loop still takes
// ~36s per set, same pace the old CSS keyframe used, whatever the icon count.
const STACK_LOOP_SECONDS = 36;

function StackStrip() {
  const track = [...STACK, ...STACK];
  const trackRef = useRefHero(null);
  const st = useRefHero({ offset: 0, half: 0, speed: 0, dragging: false, dragged: false, paused: false, startX: 0, startOffset: 0 }).current;

  useEffectHero(() => {
    const el = trackRef.current;
    if (!el) return;

    const measure = () => {
      st.half = el.scrollWidth / 2 || 1;
      st.speed = st.half / STACK_LOOP_SECONDS;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);

    const apply = () => { el.style.transform = `translateX(${st.offset}px)`; };
    const wrap = () => {
      while (st.offset <= -st.half) st.offset += st.half;
      while (st.offset > 0) st.offset -= st.half;
    };

    let raf;
    let last = null;
    const tick = (now) => {
      if (last == null) last = now;
      const dt = (now - last) / 1000;
      last = now;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const calm = document.documentElement.getAttribute("data-calm") === "on";
      if (!st.dragging && !st.paused && !reduce && !calm) {
        st.offset -= st.speed * dt;
        wrap();
        apply();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onEnter = () => { st.paused = true; };
    const onLeave = () => { st.paused = false; };

    const onDown = (e) => {
      st.dragging = true;
      st.dragged = false;
      st.startX = e.clientX;
      st.startOffset = st.offset;
      el.setPointerCapture(e.pointerId);
      el.classList.add("dragging");
    };
    const onMove = (e) => {
      if (!st.dragging) return;
      const dx = e.clientX - st.startX;
      if (Math.abs(dx) > 4) st.dragged = true;
      st.offset = st.startOffset + dx;
      wrap();
      apply();
    };
    const endDrag = (e) => {
      if (!st.dragging) return;
      st.dragging = false;
      el.classList.remove("dragging");
      try { el.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
      // a real drag (as opposed to a tap/click) leaves the button under the
      // cursor focused, which pins its tooltip open via :focus — clear it.
      if (st.dragged && el.contains(document.activeElement)) document.activeElement.blur();
      st.dragged = false;
    };

    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    el.addEventListener("focusin", onEnter);
    el.addEventListener("focusout", onLeave);
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", endDrag);
    el.addEventListener("pointercancel", endDrag);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
      el.removeEventListener("focusin", onEnter);
      el.removeEventListener("focusout", onLeave);
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", endDrag);
      el.removeEventListener("pointercancel", endDrag);
    };
  }, []);

  return (
    <div className="stack-strip">
      <div className="stack-label">// the stack &amp; tools I live in</div>
      <div className="stack-track" role="list" ref={trackRef}>
        {track.map(({ slug, label }, i) => {
          // The set is rendered twice so the loop can wrap without a seam. Only the first
          // copy is content; the second is decoration and stays out of the a11y tree.
          // Nothing here is interactive — a plain span keeps 40 dead tab stops off the page.
          const duplicate = i >= STACK.length;
          return (
            <span className="stack-item" key={slug + i}
                  role={duplicate ? undefined : "listitem"}
                  aria-hidden={duplicate ? "true" : undefined}>
              <img
                src={`/img/stack/${slug}.svg`}
                alt={label}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              />
              {/* the name is already on the img's alt — the tooltip is a visual echo */}
              <span className="stack-tip" aria-hidden="true">{label}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function HeroCopy({ onGo }) {
  // sharp, randomized glitch-tear bursts on the hero title only
  React.useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const lines = Array.from(document.querySelectorAll(".hero h1 .glitch"));
    if (!lines.length) return;
    const rnd = (a, b) => a + Math.random() * (b - a);
    const timers = [];
    const clearAll = () => { while (timers.length) clearTimeout(timers.pop()); };

    const frame = (line) => {
      // a thin random horizontal tear band that shifts left/right
      const top = rnd(0, 78);
      const h = rnd(5, 24);
      line.style.setProperty("--g-top", top.toFixed(1) + "%");
      line.style.setProperty("--g-bot", Math.max(0, 100 - top - h).toFixed(1) + "%");
      line.style.setProperty("--gx-a", rnd(-7, 7).toFixed(1) + "px");
      line.style.setProperty("--gx-b", rnd(-7, 7).toFixed(1) + "px");
      line.style.setProperty("--gy", rnd(-2, 2).toFixed(1) + "px");
      line.style.setProperty("--gx-base", rnd(-3, 3).toFixed(1) + "px");
    };

    const burst = () => {
      if (document.documentElement.getAttribute("data-calm") === "on" || reduce) { schedule(); return; }
      lines.forEach((l) => l.classList.add("glitching"));
      const steps = Math.floor(rnd(6, 11));
      let n = 0;
      const tick = () => {
        lines.forEach(frame);
        n++;
        if (n < steps) {
          timers.push(setTimeout(tick, rnd(22, 46)));   // fast, sharp frames
        } else {
          lines.forEach((l) => {
            l.classList.remove("glitching");
            ["--g-top", "--g-bot", "--gx-a", "--gx-b", "--gy", "--gx-base"].forEach((p) => l.style.removeProperty(p));
          });
          schedule();
        }
      };
      tick();
    };

    function schedule() { timers.push(setTimeout(burst, rnd(2400, 4600))); }
    // Late enough that the reveal fades have finished. Fired earlier, the torn heading
    // reads as text that has not loaded yet rather than as a deliberate effect.
    timers.push(setTimeout(burst, 1800));
    return clearAll;
  }, []);

  return (
    <React.Fragment>
      <span className="eyebrow">personal hub · QA &amp; systems</span>
      {/* The glitch layers copy the text into ::before and ::after via content:attr(data-text),
          and Chrome puts generated content in the a11y tree — so the heading was announced three
          times over. One explicit label on the h1 names the whole subtree once. Keep it in step
          with the two lines below. */}
      <h1 aria-label="A nerd turning chaos into systems that make sense.">
        <span className="line glitch" data-text="A nerd turning chaos into">A nerd turning chaos into</span>
        <span className="line accent glitch" data-text="systems that make sense.">systems that make sense.</span>
      </h1>
      <p className="hero-sub">I turn ideas into systems and messy work into something humans can actually use.</p>
      <p className="hero-tag"><span className="c">//</span> break the problem into clean steps, then put it back so it makes sense</p>
      <div className="hero-cta">
        <a className="btn btn-primary" href="#projects" onClick={(e) => onGo(e, "projects")}>Projects <i data-lucide="arrow-right"></i></a>
        <a className="btn btn-ghost" href="#notes" onClick={(e) => onGo(e, "notes")}><span className="br">~/</span>notes</a>
        <a className="btn btn-ghost" href="#contact" onClick={(e) => onGo(e, "contact")}><span className="br">$</span> contact</a>
      </div>
      {/* The same component the footers use, not a second rendering of the same data — the hero
          must read identically to every other presence spot, and sharing the component is the
          only way that stays true. .hero-centered/.hero-split already supply the top margin. */}
      <PresenceBadge className="hero-status" />
    </React.Fragment>
  );
}

// static whoami panel for the split variant
function WhoamiPanel() {
  const presence = usePresence();
  return (
    <div className="term" aria-hidden="true">
      <div className="term-bar">
        <span className="term-dot r"></span><span className="term-dot y"></span><span className="term-dot g"></span>
        <span className="term-title">~/sirucka — zsh</span>
      </div>
      <div className="term-body">
        <div className="term-line"><span className="pr">$</span> whoami</div>
        <div className="term-line term-out"><span className="v">qa-engineer</span> · automation · system-builder</div>
        <div className="term-line"><span className="pr">$</span> cat focus.json</div>
        <div className="term-line term-out">{"{"}</div>
        <div className="term-line term-out">{"  quality:  "}<span className="v">"find what breaks first"</span>,</div>
        <div className="term-line term-out">{"  tools:    "}<span className="v">["SortiiiQ", "MyMoney"]</span>,</div>
        <div className="term-line term-out">{"  mind:     "}<span className="v">"sustainable &gt;= heroics"</span></div>
        <div className="term-line term-out">{"}"}</div>
        <div className="term-line"><span className="pr">$</span> status --now</div>
        <div className="term-line term-out"><span className={"st is-" + presence.state}>● {presence.state}</span>{presence.message ? " — " + presence.message : ""}<span className="term-cursor"></span></div>
      </div>
    </div>
  );
}

// animated typing terminal for the terminal variant
const TERM_SCRIPT = [
  { t: "cmd", text: "whoami" },
  { t: "out", html: <span><span className="v">qa-engineer</span> -&gt; automation -&gt; system-builder</span> },
  { t: "cmd", text: "./run --suite=sirucka.cz" },
  { t: "out", html: <span><span className="ok">✓ 142 passed</span> != 0 flaky &nbsp;(142 &gt;= target)</span> },
  { t: "out", html: <span>{"  "}tools:    <span className="v">["SortiiiQ", "MyMoney"]</span>,</span> },
  { t: "cmd", text: "git log --oneline -1" },
  { t: "out", html: <span><span className="v">a1b2c3d</span> feat: order from chaos</span> },
  { t: "cmd", text: "echo $MOTTO" },
  { t: "out", html: <span>I'm a nerd and I love it.</span> },
];

function TypingTerminal() {
  const [done, setDone] = useStateHero([]);          // completed line objects
  const [typed, setTyped] = useStateHero("");        // currently-typing cmd text
  const [idx, setIdx] = useStateHero(0);
  const timers = useRefHero([]);

  useEffectHero(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setDone(TERM_SCRIPT); setIdx(TERM_SCRIPT.length); return; }
    if (idx >= TERM_SCRIPT.length) return;
    const line = TERM_SCRIPT[idx];
    if (line.t === "out") {
      const id = setTimeout(() => { setDone((d) => [...d, line]); setIdx((i) => i + 1); }, 280);
      timers.current.push(id);
      return () => clearTimeout(id);
    }
    // type the command char by char
    let ch = 0;
    const tick = () => {
      ch++;
      setTyped(line.text.slice(0, ch));
      if (ch < line.text.length) {
        const id = setTimeout(tick, 42 + Math.random() * 50);
        timers.current.push(id);
      } else {
        const id = setTimeout(() => { setDone((d) => [...d, line]); setTyped(""); setIdx((i) => i + 1); }, 420);
        timers.current.push(id);
      }
    };
    const start = setTimeout(tick, 220);
    timers.current.push(start);
    return () => clearTimeout(start);
  }, [idx]);

  useEffectHero(() => () => timers.current.forEach(clearTimeout), []);

  const finished = idx >= TERM_SCRIPT.length;
  return (
    <div className="term-wrap">
      <div className="term">
        <div className="term-bar">
          <span className="term-dot r"></span><span className="term-dot y"></span><span className="term-dot g"></span>
          <span className="term-title">~/sirucka — zsh</span>
        </div>
        <div className="term-body">
          {done.map((l, i) => (
            l.t === "cmd"
              ? <div className="term-line" key={i}><span className="pr">$</span> {l.text}</div>
              : <div className="term-line term-out" key={i}>{l.html}</div>
          ))}
          {!finished && idx < TERM_SCRIPT.length && TERM_SCRIPT[idx].t === "cmd" && (
            <div className="term-line"><span className="pr">$</span> {typed}<span className="term-cursor"></span></div>
          )}
          {finished && <div className="term-line"><span className="pr">$</span> <span className="term-cursor"></span></div>}
        </div>
      </div>
    </div>
  );
}

function Hero({ variant, onGo }) {
  if (variant === "split") {
    return (
      <header className="hero wrap" id="top">
        <div className="hero-split">
          <div><HeroCopy onGo={onGo} /></div>
          <WhoamiPanel />
        </div>
        <StackStrip />
      </header>
    );
  }
  if (variant === "terminal") {
    return (
      <header className="hero wrap hero-terminal" id="top">
        <HeroCopy onGo={onGo} />
        <TypingTerminal />
        <StackStrip />
      </header>
    );
  }
  // centered (default)
  return (
    <header className="hero wrap hero-centered" id="top">
      <HeroCopy onGo={onGo} />
      <StackStrip />
    </header>
  );
}

export { Hero };
